/**
 * Which of a station's counters are open to this account.
 *
 * A berth puts five services on screen at once, deliberately: docking is where
 * you *compare* things, and hiding four behind the fifth turns every decision
 * into a memory test. That argument is sound for somebody who knows what the
 * five are, and completely wrong for somebody who has just landed for the first
 * time and is looking at a wall of shop.
 *
 * So the wall assembles itself. A new account lands with one counter — the one
 * holding the job it just flew in — and each of the others appears the first
 * time it has something to say.
 *
 * ── Earned, then kept ───────────────────────────────────────────────────────
 *
 * `earnedPanels` reads the record and says which counters are *currently*
 * warranted. That is not the same as which are open, and the difference is the
 * whole design: a panel that appeared when you had money and vanished when you
 * spent it would be a puzzle rather than a tutorial. The account stores the
 * union of everything ever earned, and a counter that has opened once stays
 * open — including through a wreck, which is exactly when a player needs the
 * mechanic and the trader most.
 *
 * Reset is the one thing that closes them, because reset makes a new account.
 *
 * ── Why this is derived rather than fired ───────────────────────────────────
 *
 * Every trigger here is a fact about the stored record — money held, goods in
 * the hold, a dented hull, standing that has moved. Nothing has to remember to
 * announce anything, no action grows a second job, and a path somebody adds
 * later (salvage, a gift, a mission that pays in cargo) unlocks the right
 * counter without knowing this file exists.
 */
import { hullIntegrity } from './ship/hull.js';
import { STANDING_START } from './economy/standing.js';
/** Every counter, in the order a berth draws them. */
export const STATION_PANELS = [
    'missions',
    'trader',
    'reputation',
    'cargo',
    'mechanic',
];
/**
 * Open from the first landing.
 *
 * The board, and only the board. It is where the introduction's delivery is
 * handed in, so it is the one counter a brand new pilot has a reason to walk up
 * to — and jobs are the income, so it is also the right habit to form first.
 */
export const OPENING_PANELS = ['missions'];
/**
 * Counters that make no sense before another one is open.
 *
 * Only one so far, and it is about money rather than about order: a quote for a
 * repair you cannot pay for is a counter that opens to tell you no. The trader
 * is what opens on having linelits at all, so it is the honest thing to wait
 * for — a dented hull and an empty purse is a bad first impression of a
 * mechanic, not an introduction to one.
 *
 * Applied to the *open* set rather than to the earned one, which matters:
 * somebody who earned the trader and has since spent every linelit still has it
 * open, and should still meet the mechanic when they scrape something.
 */
const NEEDS = {
    mechanic: 'trader',
};
/**
 * Which counters this record has earned the right to see.
 *
 * Each condition is "this panel now has something to say", not "the player has
 * progressed enough" — there is no gate here that a player has to be good
 * enough to pass, only ones that stop a counter appearing before it means
 * anything.
 *
 * The raw reading of the facts, and not the final answer: ordering is applied
 * by `openPanels`, against everything open rather than against this. See
 * `NEEDS`.
 */
export function earnedPanels(record) {
    const open = new Set(OPENING_PANELS);
    // Money to spend. The introduction's delivery pays sixty on arrival, so the
    // trader opens on the first redemption — which is the point of paying for the
    // trip rather than handing the balance over at creation.
    if (record.wallet.carried > 0 || record.wallet.banked > 0)
        open.add('trader');
    /*
     * Anything in the hold at all, including a delivery crate.
     *
     * Mission cargo used to be excluded, on the grounds that it is shown on the
     * board that issued it. That was written when the introduction's crate was
     * *given* rather than taken — now it is the first thing a new pilot puts in
     * their hold, and it is the moment "you have a hold, and there is something in
     * it" first means anything. Opening the panel then is the whole point of the
     * panel.
     */
    if (record.cargo.length > 0)
        open.add('cargo');
    // A hull with a dent in it. Until then the mechanic has nothing to quote for,
    // and a repair counter offering to fix an undamaged ship is a counter that
    // reads as broken.
    if (record.hull && hullIntegrity(record.hull, record.blueprint) < 1)
        open.add('mechanic');
    // Somebody has an opinion. Standing only moves by delivering to an aligned
    // station, so this opens on the first job that was worth something to
    // somebody in particular.
    if (Object.values(record.factions).some((standing) => standing !== STANDING_START)) {
        open.add('reputation');
    }
    return STATION_PANELS.filter((panel) => open.has(panel));
}
/**
 * What is open, given what has been earned before and what is earned now.
 *
 * Ordered as a berth draws them rather than in the order they were unlocked, so
 * a counter appears where it will always be rather than arriving on the end and
 * moving later.
 */
export function openPanels(stored, record) {
    const open = new Set([...stored, ...earnedPanels(record)]);
    /*
     * Held back until whatever they depend on is open.
     *
     * Repeated until nothing changes rather than done once, so a chain of
     * prerequisites resolves in any order. One pass would be enough for the single
     * rule there is today and would quietly be wrong the moment there are two.
     */
    for (let settling = true; settling;) {
        settling = false;
        for (const [panel, needs] of Object.entries(NEEDS)) {
            if (open.has(panel) && !open.has(needs)) {
                open.delete(panel);
                settling = true;
            }
        }
    }
    return STATION_PANELS.filter((panel) => open.has(panel));
}
/** Whether every counter is open, which is the state an old account is in. */
export function allPanelsOpen(open) {
    return STATION_PANELS.every((panel) => open.includes(panel));
}
//# sourceMappingURL=stationPanels.js.map